/**
 * Search Overlay — full-screen challenge search with debounced input, tags, results.
 * Spec: challenges .txt § 12.
 */

import { useState, useEffect, useRef, useCallback, memo } from 'react'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { Search, X } from 'lucide-react'
import { BrowseChallengeCard } from './browse-challenge-card'
import { useSearchChallenges } from '@turnifly/shared/hooks'
import { Skeleton } from '@turnifly/shared/ui'

const POPULAR_TAGS = ['Sport', 'Kitob', 'IELTS', 'Ruhiy', 'Sog\'liq', 'IT', 'Biznes', 'Ozish']

interface SearchOverlayProps {
  isOpen: boolean
  onClose: () => void
}

export const SearchOverlay = memo(function SearchOverlay({
  isOpen,
  onClose,
}: SearchOverlayProps): React.ReactElement | null {
  const { t } = useTranslation()
  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  // Debounce 300ms
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query.trim()), 300)
    return () => clearTimeout(timer)
  }, [query])

  // Focus on open
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100)
    } else {
      setQuery('')
      setDebouncedQuery('')
    }
  }, [isOpen])

  const { data: results, isLoading } = useSearchChallenges(debouncedQuery)

  const handleTagClick = useCallback((tag: string): void => {
    setQuery(tag)
  }, [])

  if (!isOpen) return null

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className="fixed inset-0 z-50 bg-[var(--color-bg)] overflow-y-auto"
    >
      {/* Search header */}
      <div className="sticky top-0 z-10 bg-[var(--color-bg)]/95 backdrop-blur-sm border-b border-white/[0.04]">
        <div className="max-w-[600px] mx-auto px-4 py-3 flex items-center gap-3">
          <div className="flex-1 relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-hint" />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('challenges.browse.searchPlaceholder')}
              className="w-full pl-9 pr-3 py-2.5 rounded-xl bg-white/[0.04] border border-white/[0.06] text-[14px] text-text placeholder:text-text-hint/50 focus:outline-none focus:border-primary/30"
            />
          </div>
          <button
            onClick={onClose}
            className="shrink-0 w-9 h-9 flex items-center justify-center rounded-full bg-white/[0.04] text-text-hint tap-highlight-transparent"
          >
            <X size={18} />
          </button>
        </div>
      </div>

      <div className="max-w-[600px] mx-auto px-4 pt-4 pb-28">
        {/* Popular tags (shown when no query) */}
        {!debouncedQuery && (
          <div>
            <p className="text-[12px] font-semibold text-text-hint mb-3">
              {t('challenges.browse.popularTags')}
            </p>
            <div className="flex flex-wrap gap-2">
              {POPULAR_TAGS.map((tag) => (
                <button
                  key={tag}
                  onClick={() => handleTagClick(tag)}
                  className="px-3 py-1.5 rounded-full bg-white/[0.04] border border-white/[0.06] text-[12px] text-text-hint hover:bg-white/[0.06] transition-colors tap-highlight-transparent"
                >
                  #{tag}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Loading */}
        {debouncedQuery.length >= 2 && isLoading && (
          <div className="grid grid-cols-2 gap-2.5 mt-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-[180px] rounded-xl" />
            ))}
          </div>
        )}

        {/* Results */}
        {debouncedQuery.length >= 2 && !isLoading && (
          <div className="mt-4">
            {(results?.length ?? 0) === 0 ? (
              <div className="text-center py-12">
                <p className="text-[14px] text-text-hint">{t('challenges.browse.noResults')}</p>
                <p className="text-[12px] text-text-hint/60 mt-1">
                  {t('challenges.browse.noResultsDesc')}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2.5">
                {(results ?? []).map((c) => (
                  <BrowseChallengeCard key={c.id} challenge={c} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Hint when typing less than 2 chars */}
        {debouncedQuery.length > 0 && debouncedQuery.length < 2 && (
          <p className="text-center text-[12px] text-text-hint/50 mt-8">
            {t('challenges.browse.searchPlaceholder')}
          </p>
        )}
      </div>
    </motion.div>
  )
})
